import { MouseEvent, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Toggle, Dropdown, Tooltip, TooltipProps, IconName } from '@ets/design-system';

export type Option = {
  order?: number;
  tooltip?: Omit<TooltipProps, 'children'>;
  options?: Option[];
  testId: string;
  icon?: IconName;
  size?: 'large' | 'medium';
} & (
  | {
      inactive?: never | false;
      onClick: (e: MouseEvent<HTMLDivElement>) => void;
      label: TranslationMessage;
      disabled: boolean;
    }
  | {
      inactive?: true;
      onClick?: (e: MouseEvent<HTMLDivElement>) => void;
      label?: TranslationMessage;
      disabled?: boolean;
    }
);
type DefaultOption = Omit<Option, 'label' | 'testId'> & {
  label?: Option['label'];
};

export type OptionsProps = {
  editOption: DefaultOption;
  deleteOption: DefaultOption;
  moreOptions?: Option[];
  disabled?: boolean;
  testId: string;
};

const Options = ({ editOption, deleteOption, moreOptions, disabled, testId }: OptionsProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const options = useMemo<Option[]>(() => {
    let defaultEditOption: Option = {
      inactive: true,
      testId: `${testId}-options-toggle-edit-item`,
    };
    let defaultDeleteOption: Option = {
      inactive: true,
      testId: `${testId}-options-toggle-delete-item`,
    };

    if (!editOption.inactive && editOption.onClick && editOption.disabled != null) {
      defaultEditOption = {
        order: 1,
        label: { id: 'global.edit' },
        testId: `${testId}-options-toggle-edit-item`,
        disabled: editOption.disabled,
        onClick: editOption.onClick,
        ...editOption,
      };
    }

    if (!deleteOption.inactive && deleteOption.onClick && deleteOption.disabled != null) {
      defaultDeleteOption = {
        order: 2,
        label: { id: 'global.delete' },
        testId: `${testId}-options-toggle-delete-item`,
        disabled: deleteOption.disabled,
        onClick: deleteOption.onClick,
        ...deleteOption,
      };
    }

    let defaultOptions: Option[] = [defaultEditOption, defaultDeleteOption];

    return defaultOptions
      .concat(moreOptions ?? [])
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .filter((option) => !option.inactive);
  }, [moreOptions, editOption, deleteOption, testId]);

  const onOptionClick = (e: MouseEvent<HTMLDivElement>, option: Option) => {
    e.stopPropagation();
    option.onClick?.(e);
  };

  const generateOptionItem = (option: Option, id: number) => {
    const item = (
      <Dropdown.Item
        key={id}
        onClick={(e) => {
          onOptionClick(e, option);
        }}
        disabled={option.disabled}
        testId={option.testId}
        prefix={option.icon}
      >
        <FormattedMessage id={option.label?.id} values={option.label?.values} />
      </Dropdown.Item>
    );

    if (option.tooltip) {
      return (
        <Tooltip
          {...option.tooltip}
          visible={option.tooltip ? option.tooltip.visible : true}
          key={id}
        >
          <span>{item}</span>
        </Tooltip>
      );
    }

    return item;
  };

  const renderItem = (option: Option, id: number) => {
    return option.options?.length ? (
      <Dropdown.Sub>
        <Dropdown.SubTrigger
          key={id}
          onClick={(e) => {
            onOptionClick(e, option);
          }}
          disabled={option.disabled}
          prefix={option.icon}
          testId={`${testId}-options-toggle-${option.testId}-submenu`}
        >
          <FormattedMessage id={option.label?.id} values={option.label?.values} />
        </Dropdown.SubTrigger>
        <Dropdown.SubContent testId={`${testId}-options-toggle-${option.testId}-submenu-content`}>
          {option.options.map((subOption, j) =>
            renderItem(
              {
                ...subOption,
                testId: `${testId}-options-toggle-${option.testId}-submenu-${subOption.testId}-item`,
              },
              j,
            ),
          )}
        </Dropdown.SubContent>
      </Dropdown.Sub>
    ) : (
      generateOptionItem(
        { ...option, testId: `${testId}-options-toggle-${option.testId}-item` },
        id,
      )
    );
  };

  return (
    <Dropdown.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dropdown.Trigger>
        <Toggle
          variant="link"
          size="m"
          iconProps={{
            icon: 'ellipsis',
          }}
          isToggled={isOpen}
          disabled={disabled}
          testId={`${testId}-options`}
        />
      </Dropdown.Trigger>
      <Dropdown.Content
        align="end"
        style={{ width: '30rem' }}
        testId={`${testId}-options-dropdown`}
      >
        {options.map((option, i) => {
          return renderItem(option, i);
        })}
      </Dropdown.Content>
    </Dropdown.Root>
  );
};

export default Options;
